import React, { useEffect, useRef, useState } from "react";
import { VaiLiveSession } from "../voice/liveSession";
import { avatarFor } from "../utils/vaiAvatar";
import ModeSwitch from "./ModeSwitch";
import "./VoiceStage.css";

const STATUS_TEXT = {
  "requesting-mic": "Enabling microphone…",
  connecting: "Connecting to vAI…",
  live: "Listening…",
  speaking: "Speaking…",
  error: "Something went wrong",
  ended: "Voice mode ended",
};

/** Map a live session status onto the vAI avatar states. */
export function voiceAvatarState(status, thinking) {
  if (status === "speaking") return "speaking";
  if (status === "requesting-mic" || status === "connecting" || thinking)
    return "thinking";
  return "idle";
}

function appendWord(prev, chunk) {
  return prev ? prev + " " + chunk : chunk;
}

/**
 * Voice mode stage: a real mode switch, not dictation. The text composer is
 * gone while this is mounted, so the keyboard never appears.
 *
 * The transcript shows vAI's replies as a rolling window of the last three
 * turns — it never grows without bound, and the user's own speech isn't
 * shown. The full exchange is still handed to onExit as { userText, vaiText }
 * so ending voice mode can log it to the chat.
 */
const MAX_TURNS = 3;

export default function VoiceStage({ onExit }) {
  const [vStatus, setVStatus] = useState("requesting-mic");
  const [statusDetail, setStatusDetail] = useState("");
  const [turns, setTurns] = useState([]);
  const [liveText, setLiveText] = useState("");
  const [thinking, setThinking] = useState(false);
  const sessionRef = useRef(null);
  const textsRef = useRef({ userText: "", vaiText: "" });
  const liveRef = useRef("");
  const turnId = useRef(0);
  // Timestamps for the "thinking" watchdog: the user spoke, but the model
  // hasn't answered yet. Without this the stage looks dead during model
  // latency or a stalled turn.
  const lastInputAt = useRef(0);
  const lastModelAt = useRef(0);
  const vStatusRef = useRef("requesting-mic");

  useEffect(() => {
    const session = new VaiLiveSession("", {
      onStatus: (s, detail) => {
        vStatusRef.current = s;
        setVStatus(s);
        setStatusDetail(detail || "");
        if (s === "speaking" || s === "error" || s === "ended") {
          lastModelAt.current = Date.now();
          setThinking(false);
        }
      },
      onInputTranscript: (t) => {
        // Kept for the onExit chat log; not shown on stage.
        textsRef.current.userText = appendWord(textsRef.current.userText, t);
        lastInputAt.current = Date.now();
      },
      onOutputTranscript: (t) => {
        textsRef.current.vaiText = appendWord(textsRef.current.vaiText, t);
        liveRef.current = appendWord(liveRef.current, t);
        setLiveText(liveRef.current);
        lastModelAt.current = Date.now();
        setThinking(false);
      },
      onTurnComplete: () => {
        const text = liveRef.current.trim();
        if (text) {
          turnId.current += 1;
          const entry = { id: turnId.current, text };
          setTurns((prev) => [...prev, entry].slice(-MAX_TURNS));
        }
        liveRef.current = "";
        setLiveText("");
        lastModelAt.current = Date.now();
        setThinking(false);
      },
      onInterrupted: () => {
        // The model was cut off mid-turn: the partial text is a fragment,
        // not a reply, so drop it instead of finalizing it. Otherwise the
        // restarted reply shows up as a second, duplicate turn.
        liveRef.current = "";
        setLiveText("");
        lastModelAt.current = Date.now();
        setThinking(false);
      },
    });
    sessionRef.current = session;
    session.start();
    const watchdog = window.setInterval(() => {
      if (
        vStatusRef.current === "live" &&
        lastInputAt.current > lastModelAt.current &&
        Date.now() - lastInputAt.current > 2500
      ) {
        setThinking(true);
      }
    }, 1000);
    return () => {
      window.clearInterval(watchdog);
      session.stop();
    };
  }, []);

  const end = () => {
    if (sessionRef.current) sessionRef.current.stop();
    onExit({
      userText: textsRef.current.userText.trim(),
      vaiText: textsRef.current.vaiText.trim(),
    });
  };

  const avatarState = voiceAvatarState(vStatus, thinking);
  const statusText = thinking ? "Thinking…" : STATUS_TEXT[vStatus] || vStatus;
  const pulseClass =
    vStatus === "speaking"
      ? "talking"
      : vStatus === "live"
        ? "listening"
        : "";

  return (
    <div className="voice-stage" role="dialog" aria-label="Voice mode">
      <div className="voice-stage-head">
        <span className="brand-name">vAI</span>
        <ModeSwitch mode="voice" onChange={(m) => m === "chat" && end()} />
      </div>
      <div className="voice-center">
        <div className={`voice-pulse ${pulseClass}`}>
          <span className="voice-ring" aria-hidden="true" />
          <img src={avatarFor(avatarState)} alt="" aria-hidden="true" />
        </div>
        <div className="voice-status">{statusText}</div>
        <div className="voice-hint">
          {vStatus === "error"
            ? statusDetail || "Please try again."
            : vStatus === "speaking"
              ? "Interrupt anytime — just speak"
              : "Speak naturally — vAI hears you"}
        </div>
        <div className="voice-transcript" aria-live="polite">
          {turns.map((t) => (
            <div className="voice-line" key={t.id}>
              <span className="who">vAI</span>
              <p>{t.text}</p>
            </div>
          ))}
          {liveText && (
            <div className="voice-line" key="live">
              <span className="who">vAI</span>
              <p>
                {liveText}
                <span className="caret" aria-hidden="true" />
              </p>
            </div>
          )}
          {turns.length === 0 && !liveText && (
            <p className="voice-empty">vAI&rsquo;s replies will appear here…</p>
          )}
        </div>
      </div>
      <div className="voice-controls">
        <button type="button" className="voice-end" onClick={end}>
          End voice mode
        </button>
      </div>
    </div>
  );
}
